// ============================================================================
// columnLabel — turn a database column name into something a person can read.
//
// WHY THIS EXISTS. `errors.ts` translates database violations into sentences,
// and to write a sentence it needs a name for the field. It used to hold a
// hand-written map of about a dozen columns, all from facility_tasks — the one
// screen it was written for. Every other column fell through to the RAW COLUMN
// NAME, slotted into a confident sentence:
//
//     "Couldn't save — vehicle_id is required and was left empty."
//
// That reads as an app validation rule. It is not one. It was Postgres
// rejecting an insert, and the sentence sent two people in a row hunting for a
// form field that does not exist (2026-08-24, Fleet). The failure mode is not
// "confusing message" — it is a CONFIDENT WRONG ANSWER, and it looks exactly
// as trustworthy as a correct one.
//
// WHY A FUNCTION AND NOT A BIGGER MAP. 152 of the 175 tables are writable from
// the browser and carry 1,253 distinct column names (measured 2026-08-24). A
// hand-written map of that is unmaintainable and, worse, silently incomplete —
// the next column added to any table falls straight back to the raw name and
// the whole failure repeats. So: derive the label, and keep an override map
// only for the names where deriving is wrong.
//
// THE GUARANTEE: this never returns a raw snake_case identifier. Tested.
// ============================================================================

/**
 * Names where the mechanical result is wrong or clumsy. KEEP THIS SHORT — if
 * you are adding an entry that the deriver would have got right anyway, don't.
 * The first block is the original facility_tasks map, preserved verbatim so the
 * sentences those screens already produce do not change.
 */
const OVERRIDES: Record<string, string> = {
  // --- original map (facility_tasks), unchanged -----------------------------
  assigned_to: 'Assign to',
  created_by: 'Created by',
  completed_by: 'Completed by',
  facility_id: 'Facility',
  priority: 'Priority',
  status: 'Status',
  category: 'Category',
  source: 'Source',
  due_date: 'Due date',
  // facility_tasks stores the task text in `description`, not `title` —
  // confirmed against the live column list 2026-07-29.
  description: 'Task description',
  completion_notes: 'Completion notes',

  // --- names the deriver gets wrong ----------------------------------------
  eid: 'Employee',
  vin: 'VIN',
  sqft: 'Square footage',
  monthly_rate: 'Monthly rate',
  bill_amount: 'Bill amount',
  special_pay: 'Special pay',
  qbo_synced: 'QuickBooks sync flag',
  tcs_provides_usables: 'TCS provides usables',
  next_kicker_date: 'Next kicker date',
  service_state: 'Service state',
  end_reason_code: 'End reason',
  recipient_eid: 'Recipient',
  reported_by_eid: 'Reported by',
  last_mileage_by_eid: 'Odometer last reported by',
  assigned_employee_eid: 'Assigned to',
};

/**
 * Columns nobody could have typed. A violation on one of these is never the
 * user's fault, so it gets a different sentence — see systemAssignedColumn().
 * `vehicle_id` is the worked example: it is a primary key minted by a trigger,
 * and telling somebody they left it empty is simply false.
 */
const SYSTEM_SUFFIXES = ['_at', '_id'];
const SYSTEM_EXACT = new Set([
  'created_at',
  'updated_at',
  'created_by',
  'last_updated',
  'id',
]);

/** Word-level fixes. Anything not here is passed through as-is. */
const GLOSSARY: Record<string, string> = {
  am: 'AM',
  ap: 'AP',
  ar: 'AR',
  coi: 'COI',
  dob: 'date of birth',
  ein: 'EIN',
  hr: 'HR',
  num: 'number',
  pct: 'percent',
  pdf: 'PDF',
  po: 'PO',
  poc: 'point of contact',
  qbo: 'QuickBooks',
  qr: 'QR',
  qty: 'quantity',
  rag: 'RAG',
  sop: 'SOP',
  sqft: 'square footage',
  ssn: 'SSN',
  url: 'link',
  vin: 'VIN',
  w9: 'W-9',
  exp: 'expiry',
  eid: 'employee',
  tcs: 'TCS',
};

/** True when the column is one the system fills in, not the person. */
export function isSystemAssignedColumn(column: string): boolean {
  const c = column.trim().toLowerCase();
  if (SYSTEM_EXACT.has(c)) return true;
  // A trailing _id or _at on its own is machinery. A FOREIGN key like
  // facility_id is also machinery from the user's point of view — they picked
  // a facility, they did not type an id — but it IS something they chose, so
  // those keep the ordinary "pick it from the list" sentence and are excluded
  // here by the override map having a human label for them.
  if (OVERRIDES[c]) return false;
  return SYSTEM_SUFFIXES.some((s) => c.endsWith(s) && c.length > s.length);
}

function humanize(column: string): string {
  const parts = column.split('_').filter(Boolean);

  // Strip the grammar that carries no meaning in a sentence.
  if (parts.length > 1 && (parts[0] === 'is' || parts[0] === 'has')) parts.shift();
  while (
    parts.length > 1 &&
    (parts[parts.length - 1] === 'id' || parts[parts.length - 1] === 'at')
  ) {
    parts.pop();
  }
  // A trailing `eid` means "this is an employee reference" — the word adds
  // nothing once the rest of the name says who.
  if (parts.length > 1 && parts[parts.length - 1] === 'eid') parts.pop();

  const words = parts.map((p) => GLOSSARY[p] ?? p);
  if (words.length === 0) return column;

  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/**
 * A human name for a database column.
 *
 * Never returns a raw snake_case identifier — that is the whole point. An
 * unrecognised column is derived, not passed through.
 */
export function columnLabel(column: string | null | undefined): string | null {
  if (!column) return null;
  const c = column.trim();
  if (c === '') return null;
  const key = c.toLowerCase();
  if (OVERRIDES[key]) return OVERRIDES[key];
  return humanize(key);
}
